import React, { useEffect, useRef } from 'react'
import { useMount } from 'react-use'

import LoadingWidget from '../components/widgets/LoadingWidget'
import useAppDispatch from '../hooks/useAppDispatch'
import useAppSelector from '../hooks/useAppSelector'
import { useCommonDataQuery } from '../hooks/useCommonDataQuery'
import { useCreateSessionMetadataMutation } from '../hooks/useCreateSessionMetadataMutation'
import { useIsPdf } from '../hooks/useIsPdf'
import { useOnboardingChecklistQuery } from '../hooks/useOnboardingChecklistQuery'
import { getIdFromIri } from '../lib/helpers/helperFunctions'
import authService, {
  AuthSessionIsAuthenticated,
} from '../lib/services/authService'
import { selectIsCommonDataLoaded } from '../redux/slices/commonData'
import { setOnboardingChecklist } from '../redux/slices/onboardingChecklist'
import { setIsAuthenticated } from '../redux/slices/session'

interface Props {
  children: React.ReactNode
}

const SessionProvider: React.FC<Props> = ({ children }) => {
  const initialiseSessionMutation = useCreateSessionMetadataMutation()
  const initialiseSessionMutationRef = useRef(initialiseSessionMutation)
  const commonDataQuery = useCommonDataQuery()
  const isPdf = useIsPdf()
  const dispatch = useAppDispatch()
  const isCommonDataLoaded = useAppSelector(selectIsCommonDataLoaded)

  // Listen for session authentication events and update Redux accordingly
  authService.addEventListener('sessionIsAuthenticated', (event: Event) => {
    const customEvent = event as CustomEvent<AuthSessionIsAuthenticated>
    const status = customEvent.detail.status

    dispatch(setIsAuthenticated(status))
  })

  const onboardingChecklistIri =
    initialiseSessionMutation.data?.onboardingChecklist
  const onboardingChecklistId = onboardingChecklistIri
    ? getIdFromIri(onboardingChecklistIri)
    : undefined
  const onboardingChecklistQuery = useOnboardingChecklistQuery(
    onboardingChecklistId,
  )

  // Initialise the session on mount
  useMount(() => {
    if (!isPdf) {
      initialiseSessionMutationRef.current.mutate()
    }
  })

  // Update the onboarding checklist in the Redux store once we get the data
  // from the API
  useEffect(() => {
    if (onboardingChecklistQuery.data) {
      dispatch(setOnboardingChecklist(onboardingChecklistQuery.data))
    }
  }, [dispatch, onboardingChecklistQuery.data])

  const isLoading =
    initialiseSessionMutation.isPending ||
    !isCommonDataLoaded ||
    onboardingChecklistQuery.isLoading

  if (isLoading) {
    return <LoadingWidget />
  }

  const error =
    initialiseSessionMutation.error ||
    commonDataQuery.error ||
    onboardingChecklistQuery.error
  if (error) {
    throw error
  }

  return <>{children}</>
}

export default SessionProvider
